import {
  HHAlert,
  HHButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useHingeSelectClient } from '../../api';
import { Item } from '../../api/types';

const monoCell = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontVariantNumeric: 'tabular-nums' as const,
  fontSize: 13,
};

export interface KGItemPickerProps {
  disabledIds: Set<string>;
  onSelect: (item: Item) => void;
}

const itemName = (i: Item): string =>
  i.technicalName ?? i.colloquialName ?? i.description ?? '(unnamed item)';

export const KGItemPicker = ({
  disabledIds,
  onSelect,
}: KGItemPickerProps): JSX.Element => {
  const client = useHingeSelectClient();
  const [status, setStatus] = useState<'pending' | 'loaded' | 'error'>(
    'pending',
  );
  const [items, setItems] = useState<Item[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    void (async (): Promise<void> => {
      try {
        const res = await client.listItems({
          paginationParams: { pageSize: 100 },
        });
        setItems(res.items ?? []);
        setStatus('loaded');
      } catch (e) {
        setError((e as Error).message);
        setStatus('error');
      }
    })();
  }, [client]);

  if (status === 'pending') {
    return (
      <HHStack>
        <HHTypography hhVariant="body2" sx={{ opacity: 0.7 }}>
          Loading items…
        </HHTypography>
      </HHStack>
    );
  }

  if (status === 'error') {
    return (
      <HHAlert hhVariant="outlined" severity="error">
        Failed to load items: {error}
      </HHAlert>
    );
  }

  if (items.length === 0) {
    return (
      <HHStack>
        <HHTypography hhVariant="body2">No items found.</HHTypography>
      </HHStack>
    );
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ maxHeight: 320 }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell sx={{ width: 320 }}>Item ID</TableCell>
            <TableCell sx={{ width: 80 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(i => {
            const disabled = i.id != null && disabledIds.has(i.id);
            return (
              <TableRow
                key={i.id ?? itemName(i)}
                sx={{ opacity: disabled ? 0.45 : 1 }}
              >
                <TableCell>{itemName(i)}</TableCell>
                <TableCell sx={{ opacity: 0.75 }}>
                  {i.description ?? '—'}
                </TableCell>
                <TableCell sx={monoCell}>{i.id ?? '—'}</TableCell>
                <TableCell align="right">
                  <HHButton
                    hhVariant="variant-bypass"
                    variant="outlined"
                    size="small"
                    disabled={disabled}
                    onClick={(): void => {
                      if (i.id != null) onSelect(i);
                    }}
                  >
                    {disabled ? 'Added' : 'Add'}
                  </HHButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
